import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Newspaper, Loader2, ImagePlus } from "lucide-react";
import { API_URL, LOCAL_IMAGES } from "../data/site";

const inputCls =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-[#111111] placeholder:text-neutral-400 transition-colors duration-300 focus:border-[#111111] focus:outline-none";

const labelCls = "font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500";

const EMPTY = { title: "", circuit: "", event: "", date_label: "", result: "", summary: "" };

export const AdminRecapForm = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleImage = (src) =>
    setImages((imgs) => (imgs.includes(src) ? imgs.filter((i) => i !== src) : [...imgs, src]));

  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API_URL}/uploads`, fd, { withCredentials: true });
        const url = res.data.url;
        setUploads((u) => [{ src: url, label: file.name }, ...u]);
        setImages((imgs) => (imgs.includes(url) ? imgs : [...imgs, url]));
      }
      toast.success(files.length === 1 ? "Photo uploaded and selected." : `${files.length} photos uploaded and selected.`);
    } catch {
      toast.error("Upload failed. Images only, up to 10 MB each.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/recaps`, { ...form, images }, { withCredentials: true });
      toast.success("Recap published. It's live on the Recaps page.");
      setForm(EMPTY);
      setImages([]);
      setOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not publish the recap.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-recap-form" className="mt-16 border-t border-neutral-200 pt-10">
      <button
        type="button"
        data-testid="recap-form-toggle"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#111111]"
      >
        <Newspaper className="h-4 w-4 text-[#e63946]" />
        {open ? "Close recap form" : "Post a race recap"}
      </button>

      {open && (
        <form onSubmit={submit} className="mt-8 space-y-5 border border-neutral-200 bg-[#f8f9fa] p-6 md:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="recap-title" className={labelCls}>Title *</label>
              <input id="recap-title" data-testid="recap-title-input" required minLength={4} value={form.title} onChange={update("title")} placeholder="Podium at Sebring" className={inputCls} />
            </div>
            <div>
              <label htmlFor="recap-circuit" className={labelCls}>Circuit *</label>
              <input id="recap-circuit" data-testid="recap-circuit-input" required value={form.circuit} onChange={update("circuit")} placeholder="Sebring International Raceway, Florida" className={inputCls} />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label htmlFor="recap-event" className={labelCls}>Series / Event</label>
              <input id="recap-event" data-testid="recap-event-input" value={form.event} onChange={update("event")} placeholder="World Racing League" className={inputCls} />
            </div>
            <div>
              <label htmlFor="recap-date" className={labelCls}>Date *</label>
              <input id="recap-date" data-testid="recap-date-input" required value={form.date_label} onChange={update("date_label")} placeholder="August 2026" className={inputCls} />
            </div>
            <div>
              <label htmlFor="recap-result" className={labelCls}>Result</label>
              <input id="recap-result" data-testid="recap-result-input" value={form.result} onChange={update("result")} placeholder="2nd in class" className={inputCls} />
            </div>
          </div>
          <div>
            <label htmlFor="recap-summary" className={labelCls}>Weekend Summary *</label>
            <textarea id="recap-summary" data-testid="recap-summary-input" required minLength={20} rows={4} value={form.summary} onChange={update("summary")} placeholder="What the team ran, how the weekend went, and the outcome." className={`${inputCls} resize-y`} />
          </div>
          <div>
            <p className={labelCls}>Photos (pick up to 4)</p>
            <input
              ref={fileInputRef}
              id="recap-upload"
              data-testid="recap-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={uploadFiles}
              className="hidden"
            />
            <button
              type="button"
              data-testid="recap-upload-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mb-4 inline-flex items-center gap-2 border border-dashed border-neutral-400 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#e63946] hover:text-[#e63946] disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload from this device"}
            </button>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 md:grid-cols-7">
              {[...uploads, ...LOCAL_IMAGES].map((img) => {
                const selected = images.includes(img.src);
                return (
                  <button
                    key={img.src}
                    type="button"
                    data-testid={`recap-image-${img.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleImage(img.src)}
                    aria-pressed={selected}
                    title={img.label}
                    className={`relative overflow-hidden border-2 transition-colors duration-300 ${selected ? "border-[#e63946]" : "border-transparent opacity-70 hover:opacity-100"}`}
                  >
                    <img src={img.src} alt={img.label} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="submit"
            data-testid="recap-publish-button"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#111111] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Newspaper className="h-4 w-4" />}
            Publish Recap
          </button>
        </form>
      )}
    </div>
  );
};
